/**
 * Get resolution state for a market
 */
import type { Address } from "viem"
import { type ReadableClient, readResolution } from "../../internal/contracts"
import type { Resolution } from "../../types"

export interface GetResolutionParams {
	market: Address
}

/**
 * Get the resolution state of a market
 *
 * Returns the winning outcome, resolution/finalization status, data URI, and nonce.
 *
 * @example
 * ```ts
 * import { getResolution } from "@breakout/sdk/actions/read"
 *
 * const resolution = await getResolution(publicClient, { market: "0x..." })
 * if (resolution.isResolved && !resolution.isFinalized) {
 *   console.log(`Winner: bucket ${resolution.winningOutcome}`)
 *   console.log(`Nonce: ${resolution.nonce}`)
 * }
 * ```
 */
export async function getResolution(
	client: ReadableClient,
	params: GetResolutionParams,
): Promise<Resolution> {
	const raw = await readResolution(client, params.market)

	return {
		winningOutcome: raw.winningOutcome,
		isResolved: raw.isResolved,
		isFinalized: raw.isFinalized,
		dataUri: raw.dataUri,
		nonce: raw.nonce,
	}
}
